import { LicenseSettings } from "../settings/LicenseSettings";
import { Logger } from "../logging/Logger";
import { LogManager } from "../logging/LogManager";
import { StringUtil } from "../StringUtil";

export class LicenseTester {
    private static log: Logger;

    /**
     * Tests the given class name to see if the class is licensed.
     *
     * @param className The full class name to check.
     * @return true if the class is licensed, false otherwise.
     */
    public static testClass(className: string): boolean {
        if (className == null || className.lastIndexOf(".") <= 0) {
            this.getLog().debug("Cannot test invalid class name [%o]", className);
            return false;
        }
        const licenseSettings = LicenseSettings.getSingleton();

        // If the class is licensed, that's good enough, return true.
        const classIsLicensed = licenseSettings.licensedClasses.includes(className);
        this.getLog().debug("Class [%o] is licensed: %o", className, classIsLicensed);
        if (classIsLicensed === true) {
            return true;
        }

        // If the class isn't licensed, make sure that its package is licensed, and that the class hasn't been
        // explicitly excluded.
        const packageName = className.substring(0, className.lastIndexOf('.'));
        const packageIsLicensed = this.testPackage(packageName);
        if (!packageIsLicensed) {
            return false;
        }
        const classIsExcluded = licenseSettings.excludedClasses.includes(className);
        this.getLog().debug("Class [%o] is excluded: %o", className, classIsExcluded);
        return !classIsExcluded;
    }

    /**
     * Tests the given package name to see if the package is licensed.
     *
     * @param className The full package name to check.
     * @return true if the package is licensed, false otherwise.
     */
    public static testPackage(packageName: string): boolean {
        if (StringUtil.isEmptyString(packageName)) {
            this.getLog().debug("Cannot test empty package name");
            return false;
        }
        const licenseSettings = LicenseSettings.getSingleton();
        const packageIsLicensed = licenseSettings.licensedPackages.includes(packageName);
        this.getLog().debug("Package [%o] is licensed: %o", packageName, packageIsLicensed);
        return packageIsLicensed;
    }

    private static getLog(): Logger {
        if (this.log == null) {
            this.log = LogManager.getLogger("core.licensing.LicenseTester");
        }
        return this.log;
    }
}
